import { Accordion, Card, Layout } from '@loadsmart/loadsmart-ui'

import { LaneInformation } from 'core/ui/components/stop/LaneInformation'
import { StopsTimeline } from 'core/ui/components/stop/StopsTimeline'

import analytics from 'legacy/utils/analytics'

import { type BulkRate } from '../domain/BulkRate'
import { isQuoteExpired, mapQuoteStopsToCoreStops } from '../domain/Quote'
import { getRateValue } from '../domain/Rate'
import { type SpotQuote } from '../domain/SpotQuote'
import { getStopDetailsFromStops } from '../presenter/quoteStopPresenter'
import { QuoteDetails } from './QuoteDetails'
import { QuoteNotes } from './QuoteNotes'
import { QuoteRateInputFields } from './QuoteRateInputFields'
import { SpotQuoteBidDeadline } from './SpotQuoteBidDeadline'
import { ColumnWrapper, MoreInfoSectionWrapper } from './SpotQuoteDetails.styles'
import { SubmittedRateValue } from './SubmittedRateValue'

export type SpotQuoteDetailsProps = {
  id: string
  spotQuote: SpotQuote | BulkRate
  notes?: string
  quoteType?: 'single' | 'bulk'
  isSubmitted?: boolean
  currentFlatRate?: number
  disabled?: boolean
  onNotesChange: (notes: string) => void
  onRateChange: (flatRate: number) => void
}

export function SpotQuoteDetails({
  id,
  spotQuote,
  notes,
  quoteType = 'single',
  currentFlatRate,
  isSubmitted = false,
  disabled = false,
  onRateChange,
  onNotesChange,
}: SpotQuoteDetailsProps) {
  const { flatRate, perMileRate, quote } = spotQuote
  const { stops, mileage } = quote

  const detailStops = getStopDetailsFromStops(stops)

  const initialFlatRate = getRateValue(flatRate)
  const initialPerMileRate = getRateValue(perMileRate)
  const initialNotes = notes
  const isExpiredQuote = isQuoteExpired(quote)

  function handleToggle(expanded: boolean) {
    analytics.track('Spot Quote / Details / Toggle', {
      id,
      expanded,
      quoteType,
    })
  }

  return (
    <Card>
      <Card.Body>
        <Layout.Stack justify="center">
          <Layout.Group justify="space-between">
            <LaneInformation stops={detailStops} mileage={mileage} disabled={isExpiredQuote} />
            {!isSubmitted ? (
              <QuoteRateInputFields
                mileage={mileage}
                initialFlatRateValue={initialFlatRate}
                initialRatePerMileValue={initialPerMileRate}
                disabled={disabled}
                onRateChange={onRateChange}
              />
            ) : (
              <SubmittedRateValue
                initialFlatRate={initialFlatRate}
                initialPerMileRate={initialPerMileRate}
                currentFlatRate={currentFlatRate}
                mileage={mileage}
              />
            )}
          </Layout.Group>
          <Layout.Stack>
            <Accordion onExpandedChange={handleToggle} initialExpanded>
              <Accordion.Toggle>
                <SpotQuoteBidDeadline isExpired={isExpiredQuote} deadline={quote.bidsDeadline} />
              </Accordion.Toggle>
              <Accordion.Body>
                <MoreInfoSectionWrapper>
                  <ColumnWrapper>
                    <StopsTimeline stops={mapQuoteStopsToCoreStops(spotQuote.quote)} />
                  </ColumnWrapper>
                  <ColumnWrapper>
                    <QuoteDetails quote={quote} />
                  </ColumnWrapper>
                  <ColumnWrapper>
                    <QuoteNotes
                      initialNotes={initialNotes}
                      onChange={onNotesChange}
                      disabled={isSubmitted || disabled}
                    />
                  </ColumnWrapper>
                </MoreInfoSectionWrapper>
              </Accordion.Body>
            </Accordion>
          </Layout.Stack>
        </Layout.Stack>
      </Card.Body>
    </Card>
  )
}
